/**
 * Structured editor for the `lock.cascade_policy_set` setting — replaces the raw
 * JSON text field on the SettingsPage with a per-frequency step-table editor.
 * Validation reuses @dfp/policy-engine (same rules the server coerce enforces),
 * so the operator gets instant feedback and can only Save a valid policy set.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { ApiError } from "@dfp/api-client";
import { parseCascadePolicySet } from "@dfp/policy-engine";
import type {
  CascadeDesiredState,
  CascadeFlag,
  CascadeStage,
  SerializedCascadePolicy,
  SerializedCascadePolicySet,
  SerializedCascadeStep,
} from "@dfp/policy-engine";
import { Button } from "./Button.js";
import { api } from "../lib/api.js";
import {
  ALL_DESIRED,
  ALL_FLAGS,
  ALL_STAGES,
  FREQUENCIES,
  blankStep,
  hasAnyError,
  loadEditorSet,
  policyOf,
  serializeEditorSet,
  setErrors,
  sortedSteps,
  withPolicy,
  type EditorFrequency,
} from "./cascade-editor-helpers.js";

const SETTING_KEY = "lock.cascade_policy_set";

const FLAG_LABEL: Record<CascadeFlag, string> = {
  notify_customer: "notify",
  alert_operator: "alert",
  open_dispute: "dispute",
  flag_for_repossession: "repo",
};

function formatHours(h: number): string {
  if (h === 0) return "now";
  if (h % 24 === 0) return `${h / 24}d`;
  return `${h}h`;
}

interface CascadeRowProps {
  readonly row: {
    readonly key: string;
    readonly label: string;
    readonly value: string | number | boolean | readonly string[];
    readonly hasOverride: boolean;
  };
  readonly canWrite: boolean;
}

/** SettingsPage row for the cascade policy set: header + collapsed summary or the editor. */
export function CascadePolicyRow({ row, canWrite }: CascadeRowProps): JSX.Element {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const value = typeof row.value === "string" ? row.value : "";

  const save = useMutation({
    mutationFn: (json: string) =>
      api.put<{ key: string; value: unknown }>(`/api/admin/settings/${SETTING_KEY}`, {
        value: json,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["admin", "settings"] });
      setEditing(false);
      setError(null);
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? (err.problem.message ?? "Save failed") : "Network error"),
  });
  const reset = useMutation({
    mutationFn: () => api.delete<{ key: string }>(`/api/admin/settings/${SETTING_KEY}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["admin", "settings"] }),
  });

  return (
    <div className="space-y-2 py-3">
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium text-slate-900">{row.label}</p>
          <p className="font-mono text-[10px] text-slate-500">{row.key}</p>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={`rounded px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ${
              row.hasOverride ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-600"
            }`}
          >
            {row.hasOverride ? "custom" : "default"}
          </span>
          {canWrite && !editing ? (
            <Button variant="secondary" onClick={() => setEditing(true)}>
              Edit
            </Button>
          ) : null}
          {canWrite && row.hasOverride && !editing ? (
            <Button variant="secondary" onClick={() => reset.mutate()}>
              Reset
            </Button>
          ) : null}
        </div>
      </div>
      {editing ? (
        <CascadeEditor
          initial={value}
          saving={save.isPending}
          onSave={(json) => save.mutate(json)}
          onCancel={() => {
            setEditing(false);
            setError(null);
          }}
        />
      ) : (
        <CascadeSummary value={value} />
      )}
      {error ? <p className="text-xs text-red-700">{error}</p> : null}
    </div>
  );
}

/** Collapsed one-liner: where each frequency first hard-locks. */
function CascadeSummary({ value }: { value: string }): JSX.Element {
  const parsed = parseCascadePolicySet(value);
  if (!parsed) {
    return (
      <p className="text-xs text-amber-700">
        No stored policy — the built-in default is in effect.
      </p>
    );
  }
  // A parsed set's steps carry plain fromOverdueHours / desired (only `flags`
  // is a Set), so the summary reads them directly.
  const firstHard = (
    steps: readonly { readonly fromOverdueHours: number; readonly desired: string }[],
  ): string => {
    const step = [...steps]
      .sort((a, b) => a.fromOverdueHours - b.fromOverdueHours)
      .find((s) => s.desired === "HARD_LOCKED");
    return step ? formatHours(step.fromOverdueHours) : "—";
  };
  return (
    <p className="text-[11px] text-slate-500">
      hard-lock at — daily {firstHard(parsed.byFrequency.daily.steps)} · weekly{" "}
      {firstHard(parsed.byFrequency.weekly.steps)} · monthly{" "}
      {firstHard(parsed.byFrequency.monthly.steps)}
    </p>
  );
}

function CascadeEditor({
  initial,
  saving,
  onSave,
  onCancel,
}: {
  readonly initial: string;
  readonly saving: boolean;
  readonly onSave: (json: string) => void;
  readonly onCancel: () => void;
}): JSX.Element {
  const [set, setSet] = useState<SerializedCascadePolicySet>(() => loadEditorSet(initial));
  const [freq, setFreq] = useState<EditorFrequency>("daily");
  const errors = setErrors(set);
  const policy = policyOf(set, freq);

  const updateSteps = (next: readonly SerializedCascadeStep[]): void =>
    setSet((s) => withPolicy(s, freq, { ...policyOf(s, freq), steps: next }));
  const patchStep = (i: number, patch: Partial<SerializedCascadeStep>): void =>
    updateSteps(policy.steps.map((st, idx) => (idx === i ? { ...st, ...patch } : st)));
  const removeStep = (i: number): void => updateSteps(policy.steps.filter((_, idx) => idx !== i));
  const addStep = (): void => updateSteps([...policy.steps, blankStep()]);
  const tidy = (): void => updateSteps(sortedSteps(policy.steps));
  const toggleFlag = (i: number, flag: CascadeFlag, on: boolean): void => {
    const cur = new Set(policy.steps[i]?.flags ?? []);
    if (on) cur.add(flag);
    else cur.delete(flag);
    const flags = Array.from(cur);
    updateSteps(
      policy.steps.map((st, idx) => {
        if (idx !== i) return st;
        // Omit `flags` entirely when empty — exactOptionalPropertyTypes forbids
        // setting it to `undefined`, and an absent key is the canonical "none".
        return flags.length
          ? { fromOverdueHours: st.fromOverdueHours, stage: st.stage, desired: st.desired, flags }
          : { fromOverdueHours: st.fromOverdueHours, stage: st.stage, desired: st.desired };
      }),
    );
  };

  const json = serializeEditorSet(set);
  const canSave = json !== null && !hasAnyError(errors);

  return (
    <div className="space-y-3 rounded-md border border-slate-200 bg-slate-50 p-3">
      <div className="flex flex-wrap gap-1">
        {FREQUENCIES.map((f) => (
          <button
            key={f}
            type="button"
            onClick={() => setFreq(f)}
            className={`rounded px-2 py-1 text-xs font-medium ${
              f === freq
                ? "bg-brand-600 text-white"
                : "border border-slate-300 bg-white text-slate-600"
            }`}
          >
            {f}
            {errors[f] ? " ⚠" : ""}
          </button>
        ))}
      </div>

      <table className="w-full text-xs">
        <thead>
          <tr className="text-left text-slate-500">
            <th className="py-1 pr-2 font-medium">From (h)</th>
            <th className="pr-2 font-medium">Desired</th>
            <th className="pr-2 font-medium">Stage</th>
            <th className="pr-2 font-medium">Flags</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {policy.steps.map((st, i) => (
            <tr key={i} className="border-t border-slate-200">
              <td className="py-1 pr-2">
                <input
                  type="number"
                  min={0}
                  step="any"
                  value={st.fromOverdueHours}
                  onChange={(e) => patchStep(i, { fromOverdueHours: Number(e.target.value) })}
                  className="w-20 rounded border border-slate-300 px-1 py-0.5"
                />
              </td>
              <td className="pr-2">
                <select
                  value={st.desired}
                  onChange={(e) => patchStep(i, { desired: e.target.value as CascadeDesiredState })}
                  className="rounded border border-slate-300 px-1 py-0.5"
                >
                  {ALL_DESIRED.map((d) => (
                    <option key={d} value={d}>
                      {d}
                    </option>
                  ))}
                </select>
              </td>
              <td className="pr-2">
                <select
                  value={st.stage}
                  onChange={(e) => patchStep(i, { stage: Number(e.target.value) as CascadeStage })}
                  className="rounded border border-slate-300 px-1 py-0.5"
                >
                  {ALL_STAGES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </td>
              <td className="pr-2">
                <div className="flex flex-wrap gap-1">
                  {ALL_FLAGS.map((f) => {
                    const on = (st.flags ?? []).includes(f);
                    return (
                      <label
                        key={f}
                        title={f}
                        className={`cursor-pointer rounded px-1 py-0.5 text-[10px] ${
                          on
                            ? "bg-emerald-100 text-emerald-800"
                            : "border border-slate-200 bg-white text-slate-400"
                        }`}
                      >
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={on}
                          onChange={(e) => toggleFlag(i, f, e.target.checked)}
                        />
                        {FLAG_LABEL[f]}
                      </label>
                    );
                  })}
                </div>
              </td>
              <td>
                <button
                  type="button"
                  onClick={() => removeStep(i)}
                  disabled={policy.steps.length <= 1}
                  title="remove step"
                  className="px-1 text-slate-400 hover:text-red-600 disabled:opacity-30"
                >
                  ✕
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex gap-2">
        <Button variant="secondary" onClick={addStep}>
          + Step
        </Button>
        <Button variant="secondary" onClick={tidy}>
          Sort by hours
        </Button>
      </div>

      {errors[freq] ? (
        <p className="text-xs text-red-700">⚠ {errors[freq]}</p>
      ) : (
        <CascadePreview policy={policy} />
      )}

      <div className="flex items-center gap-2 pt-1">
        <Button onClick={() => json && onSave(json)} disabled={!canSave || saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
        <Button variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
        {!canSave && !errors[freq] ? (
          <span className="text-xs text-red-700">Fix the ⚠ frequency before saving.</span>
        ) : null}
      </div>
    </div>
  );
}

function CascadePreview({ policy }: { policy: SerializedCascadePolicy }): JSX.Element {
  return (
    <div className="rounded border border-slate-200 bg-white p-2 text-[11px] text-slate-600">
      <span className="font-medium text-slate-500">Timeline:</span>{" "}
      {sortedSteps(policy.steps).map((s, i) => (
        <span key={i}>
          {i > 0 ? " → " : " "}≥{formatHours(s.fromOverdueHours)}{" "}
          <span className="font-mono">{s.desired}</span>
          {s.flags && s.flags.length > 0
            ? ` (${s.flags.map((f) => FLAG_LABEL[f]).join(", ")})`
            : ""}
        </span>
      ))}
    </div>
  );
}
